import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Alert,
  Link,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { login, isAdmin, getUserRole, getAuthHeader, getDeviceId, getSessionId } from '../../services/authService';
import { initializeSettings } from '../../services/settingsInitService';
import { initializePrivileges, syncPrivilegesFromBackend } from '../../services/privilegeService';
import TrialExpiredDialog from './TrialExpiredDialog';
import notificationService from '../../services/notificationService';

/**
 * Login form component
 * @returns {JSX.Element} Login form
 */
const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [trialExpiredDialogOpen, setTrialExpiredDialogOpen] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Attempt to login
      await login(formData.email, formData.password);

      console.log('Login successful, initializing system...');

      // Initialize company settings (currency, date format, etc.)
      try {
        await initializeSettings();
        console.log('Company settings loaded successfully');
      } catch (settingsError) {
        console.warn('Could not load company settings, using defaults:', settingsError);
        // Continue with login even if settings fail to load
      }

      // Initialize privileges for role-based access control
      try {
        initializePrivileges();
        console.log('Privileges initialized successfully');

        // CRITICAL: ALWAYS sync privileges from backend on login to ensure fresh data
        // This ensures we're always working with the latest privileges from the database
        const userRole = getUserRole();
        console.log('[LoginForm] User role after login:', userRole);

        if (userRole) {
          try {
            // Clear existing privileges to force fresh sync from backend
            console.log('[LoginForm] Clearing cached privileges to force fresh sync...');
            localStorage.removeItem(`privileges_${userRole}`);
            localStorage.removeItem(`submenu_permissions_${userRole}`);

            console.log('[LoginForm] Starting privilege sync from backend...');
            console.log('[LoginForm] API Call: GET /api/role-privileges/' + userRole);
            await syncPrivilegesFromBackend(userRole);
            console.log('[LoginForm] ✅ Privileges synced from backend for role:', userRole);

            // CRITICAL: Fix routes to remove wildcards when submenu permissions exist
            const { fixPrivilegeRoutes, getSubmenuPermissions } = await import('../../services/privilegeService');

            // Check if submenu permissions were saved
            const submenuPerms = getSubmenuPermissions(userRole);
            console.log('[LoginForm] Submenu permissions after sync:', submenuPerms.length);

            // Fix routes (this will remove wildcards if submenu permissions exist)
            fixPrivilegeRoutes(userRole);

            // Verify the fix
            const updatedPrivileges = JSON.parse(localStorage.getItem(`privileges_${userRole}`) || '{}');
            console.log('[LoginForm] Updated accessibleRoutes after fix:', updatedPrivileges.accessibleRoutes);
          } catch (syncError) {
            console.error('[LoginForm] Could not sync privileges from backend:', syncError);
            console.error('[LoginForm] Sync error details:', syncError.response?.data || syncError.message);
            // Continue with login even if backend sync fails
            // But still fix existing routes to remove wildcards
            try {
              const { fixPrivilegeRoutes } = await import('../../services/privilegeService');
              fixPrivilegeRoutes(userRole);
            } catch (fixError) {
              console.error('[LoginForm] Could not fix routes:', fixError);
            }
          }
        } else {
          console.warn('[LoginForm] No user role found after login');
        }
      } catch (privilegeError) {
        console.error('[LoginForm] Could not initialize privileges:', privilegeError);
        // Continue with login even if privileges fail to initialize
      }

      // If login succeeded, redirect to dashboard (single route; privileges control access)
      console.log('Redirecting to dashboard...');

      try {
        await notificationService.createNotification({
          type: 'LOGIN_TAKEOVER_REQUEST',
          title: 'Login request',
          message: 'Approve to switch device',
          read: false,
          data: {
            deviceId: getDeviceId(),
            sessionId: getSessionId()
          }
        });
      } catch (e) { }

      // Navigate to dashboard - all users go to /dashboard (no /admin in URL)
      setTimeout(() => {
        try {
          navigate('/dashboard');
        } catch (navError) {
          console.error('Navigation error:', navError);
          window.location.href = '/dashboard';
        }
      }, 100);

    } catch (err) {
      // Check if error is about trial expiration
      const errorMessage = err.message || '';
      const isTrialExpired = errorMessage.toLowerCase().includes('trial') &&
        (errorMessage.toLowerCase().includes('ended') ||
          errorMessage.toLowerCase().includes('expired'));

      if (isTrialExpired) {
        // Trial expired - extract subscription details from error response
        let subscriptionData = {
          email: formData.email,
        };

        // Try to extract subscription details from error response
        if (err.response && err.response.data) {
          const errorData = err.response.data;
          console.log('Trial expired error response:', errorData);
          console.log('Subscription data in response:', errorData.subscription);

          if (errorData.subscription) {
            subscriptionData = {
              ...subscriptionData,
              companyId: errorData.subscription.companyId,
              planId: errorData.subscription.planId,
              planName: errorData.subscription.planName,
              planCode: errorData.subscription.planCode,
              monthlyAmount: errorData.subscription.monthlyAmount ? parseFloat(errorData.subscription.monthlyAmount) : null,
              yearlyAmount: errorData.subscription.yearlyAmount ? parseFloat(errorData.subscription.yearlyAmount) : null,
              currency: errorData.subscription.currency || 'INR',
            };
            console.log('Extracted subscription data:', subscriptionData);
          } else {
            console.warn('Subscription details not found in error response. Full error data:', errorData);
          }
        } else {
          console.warn('No error response data found. Error object:', err);
        }

        setSubscriptionData(subscriptionData);
        setTrialExpiredDialogOpen(true);
        setError(''); // Clear error to hide the alert
      } else if (err.message && (err.message.includes('Failed to fetch') || err.name === 'TypeError')) {
        setError('Network error: CORS issue or server is unavailable. Please try again or contact support.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box component="div">
      <Typography variant="h5" component="h2" gutterBottom align="center">
        Welcome Back
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom align="center" sx={{ mb: 3 }}>
        Please login to your account
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          id="email"
          name="email"
          label="Email Address"
          variant="outlined"
          margin="normal"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
          required
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          id="password"
          name="password"
          label="Password"
          variant="outlined"
          margin="normal"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          placeholder="Enter your password"
          required
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2">Remember me</Typography>}
          />
          <Link href="#" variant="body2" underline="hover">
            Forgot Password?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            py: 1.8, // Taller button
            mt: 2,
            mb: 2,
            background: 'linear-gradient(45deg, #1565c0 30%, #0d47a1 90%)',
            boxShadow: '0 4px 12px rgba(21, 101, 192, 0.3)',
            color: 'white',
            fontWeight: 700,
            letterSpacing: '0.5px',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(45deg, #0d47a1 30%, #0a3375 90%)',
              boxShadow: '0 6px 16px rgba(13, 71, 161, 0.4)',
              transform: 'translateY(-1px)'
            }
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
        </Button>
      </Box>

      {/* Trial Expired Dialog */}
      <TrialExpiredDialog
        open={trialExpiredDialogOpen}
        onClose={() => setTrialExpiredDialogOpen(false)}
        subscriptionData={subscriptionData}
      />
    </Box>
  );
};

export default LoginForm; 
